'use server'

import { apiRequest, ApiResponse } from '../request'

export type CoparticipationType =
    | 'PERCENTAGE'
    | 'FIXED_VALUE'
    | 'MIXED'
    | 'CEILING'
    | 'DEDUCTIBLE'

export interface CoparticipationRule {
    id: number
    companyId: number
    healthPlanId: number
    healthPlan?: {
        id: number
        name: string
    }
    name: string
    type: CoparticipationType
    percentageValue?: number
    fixedValue?: number
    ceilingValue?: number
    deductibleValue?: number
    procedureCodes?: string[]
    orderCategories?: string[]
    appliesToConsultation: boolean
    appliesToExam: boolean
    appliesToOrder: boolean
    appliesToHospitalization: boolean
    active: boolean
    validFrom?: string
    validUntil?: string
    createdAt: string
    updatedAt: string
    _count?: {
        calculations: number
    }
}

export interface CoparticipationCalculation {
    id: number
    companyId: number
    ruleId: number
    rule?: {
        id: number
        name: string
        type: CoparticipationType
    }
    clientId: number
    client?: {
        id: number
        name: string
    }
    serviceType: string
    serviceId: number
    procedureCode?: string
    procedureName?: string
    originalValue: number
    calculatedValue: number
    insuranceValue: number
    patientValue: number
    charged: boolean
    chargedAt?: string
    paymentId?: string
    notes?: string
    createdAt: string
    updatedAt: string
}

export interface CreateCoparticipationRuleData {
    healthPlanId: number
    name: string
    type: CoparticipationType
    percentageValue?: number
    fixedValue?: number
    ceilingValue?: number
    deductibleValue?: number
    procedureCodes?: string[]
    orderCategories?: string[]
    appliesToConsultation?: boolean
    appliesToExam?: boolean
    appliesToOrder?: boolean
    appliesToHospitalization?: boolean
    validFrom?: string
    validUntil?: string
}

export interface UpdateCoparticipationRuleData
    extends Partial<Omit<CreateCoparticipationRuleData, 'healthPlanId'>> {
    active?: boolean
}

export interface CoparticipationRuleFilters {
    healthPlanId?: number
    type?: CoparticipationType
    active?: boolean
}

export interface CoparticipationCalculationFilters {
    ruleId?: number
    clientId?: number
    serviceType?: string
    charged?: boolean
    startDate?: string
    endDate?: string
}

export interface CalculateCoparticipationData {
    healthPlanId: number
    clientId: number
    serviceType: 'CONSULTATION' | 'EXAM' | 'PROCEDURE' | 'HOSPITALIZATION'
    serviceId: number
    procedureCode?: string
    procedureName?: string
    originalValue: number
}

export interface CalculationResult {
    ruleId: number
    ruleName: string
    ruleType: CoparticipationType
    originalValue: number
    calculatedValue: number
    insuranceValue: number
    patientValue: number
}

// Rules CRUD
export async function getCoparticipationRules(
    filters?: CoparticipationRuleFilters
): Promise<ApiResponse<CoparticipationRule[]>> {
    return apiRequest<CoparticipationRule[]>('billing/coparticipation/rules', {
        params: filters,
    })
}

export async function getCoparticipationRule(
    id: number
): Promise<ApiResponse<CoparticipationRule>> {
    return apiRequest<CoparticipationRule>(
        `billing/coparticipation/rules/${id}`,
        {}
    )
}

export async function createCoparticipationRule(
    data: CreateCoparticipationRuleData
): Promise<ApiResponse<CoparticipationRule>> {
    return apiRequest<CoparticipationRule>(
        'billing/coparticipation/rules',
        { data },
        'post'
    )
}

export async function updateCoparticipationRule(
    id: number,
    data: UpdateCoparticipationRuleData
): Promise<ApiResponse<CoparticipationRule>> {
    return apiRequest<CoparticipationRule>(
        `billing/coparticipation/rules/${id}`,
        { data },
        'patch'
    )
}

export async function deleteCoparticipationRule(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`billing/coparticipation/rules/${id}`, {}, 'delete')
}

// Calculate
export async function calculateCoparticipation(
    data: CalculateCoparticipationData
): Promise<ApiResponse<CalculationResult>> {
    return apiRequest<CalculationResult>(
        'billing/coparticipation/calculate',
        { data },
        'post'
    )
}

// Calculations (history)
export async function getCoparticipationCalculations(
    filters?: CoparticipationCalculationFilters
): Promise<ApiResponse<CoparticipationCalculation[]>> {
    return apiRequest<CoparticipationCalculation[]>(
        'billing/coparticipation/calculations',
        { params: filters }
    )
}

export async function getCoparticipationCalculation(
    id: number
): Promise<ApiResponse<CoparticipationCalculation>> {
    return apiRequest<CoparticipationCalculation>(
        `billing/coparticipation/calculations/${id}`,
        {}
    )
}

// Mark as charged
export async function markCalculationAsCharged(
    id: number,
    paymentId?: string
): Promise<ApiResponse<CoparticipationCalculation>> {
    return apiRequest<CoparticipationCalculation>(
        `billing/coparticipation/calculations/${id}/charge`,
        { data: { paymentId } },
        'post'
    )
}

// Labels
export const COPARTICIPATION_TYPE_LABELS: Record<CoparticipationType, string> = {
    PERCENTAGE: 'Percentual',
    FIXED_VALUE: 'Valor Fixo',
    MIXED: 'Misto',
    CEILING: 'Teto',
    DEDUCTIBLE: 'Franquia',
}

export const COPARTICIPATION_TYPE_DESCRIPTIONS: Record<
    CoparticipationType,
    string
> = {
    PERCENTAGE: 'O paciente paga um percentual do valor total',
    FIXED_VALUE: 'O paciente paga um valor fixo por procedimento',
    MIXED: 'Combina percentual e valor fixo',
    CEILING: 'O paciente paga ate um valor maximo',
    DEDUCTIBLE: 'O paciente paga um valor inicial (franquia)',
}
